// Where the active workspace lives between page loads.
//
// Its own module because unrelated files need the same keys and must never drift: the context
// writes them when the user picks a client, and createApiClient reads one to set the
// X-Orbit-Client header on every request. A typo in either place would send requests scoped to
// a client the selector is not showing.
//
// TWO KEYS, because the allowed client list now differs per module (Client Management decides
// which tools each client appears in). PREFERRED is what the user actually chose; EFFECTIVE is
// what the module currently open can actually use. Collapsing them into one would mean simply
// visiting a tool where your client is disabled silently rewrites your choice in the tool you
// came from — you would return to find yourself in a different workspace than you left.

export const WORKSPACE_CLIENT_KEY = 'orbit_workspace_client_id';   // preferred — user's own pick
export const ACTIVE_CLIENT_KEY    = 'orbit_active_client_id';      // effective — resolved per module

// Competitive Monitoring had its own selector under this key before the workspace was global.
// Read once, so a CM user's existing selection carries over instead of resetting on upgrade.
export const LEGACY_CM_KEY = 'cm_selected_client_id';

// tx_clients.id is a UUID. Stored values are validated against this before going on a header,
// because localStorage is user-writable and a value containing a newline throws inside fetch()
// — which would break every request in the app, not just the scoped ones.
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function read(key) {
  try {
    const v = localStorage.getItem(key);
    return v && UUID_RE.test(v) ? v : null;
  } catch {
    return null;   // private mode, storage disabled
  }
}

function write(key, id) {
  try {
    if (id) localStorage.setItem(key, String(id));
    else localStorage.removeItem(key);
  } catch { /* storage disabled — selection just will not persist */ }
}

/** The user's own last explicit pick. Written only when they choose from the selector. */
export function readStoredClientId() {
  return read(WORKSPACE_CLIENT_KEY) || read(LEGACY_CM_KEY);
}

export function writeStoredClientId(id) {
  write(WORKSPACE_CLIENT_KEY, id);
}

/**
 * The workspace requests are actually scoped to: the preferred client when the current module
 * allows it, otherwise whatever the provider fell back to. This is what goes on the header.
 */
export function readEffectiveClientId() {
  return read(ACTIVE_CLIENT_KEY) || readStoredClientId();
}

export function writeEffectiveClientId(id) {
  write(ACTIVE_CLIENT_KEY, id);
}
